import { Dropdown, Tooltip } from 'antd/lib';
import { useEffect, useState } from 'react';
import {
  AlertCircle,
  Check,
  CheckCircle,
  Info,
  Loader,
  MoreHorizontal,
  Plus,
  Star,
  Trash2,
  X
} from 'react-feather';

import { Button, Spinner } from '@/components';
import { TextInput } from '@/components/text-input';
import { useAppContext } from '@/context';
import { useTranslation } from 'react-i18next';
import { useAddEmail } from '@/hooks/email/useAddEmail';
import { useForgetEmail } from '@/hooks/email/useForgetEmail';
import { useMakePrimaryEmail } from '@/hooks/email/useMakePrimaryEmail';
import { useVerifyEmail } from '@/hooks/email/useVerifyEmail';
import { emailRegex } from '@/utils/validation';

import EmailVerificationModal from '../modals/EmailVerificationModal';
import styles from '../Settings.module.css';

interface EmailSectionProps {
  onUpdate: (user: Api.UserObject) => void;
  user: Api.UserObject;
}

export function EmailSection({ onUpdate, user }: EmailSectionProps) {
  const [emails, setEmails] = useState<Api.EmailType[]>(() => {
    const sortedData: Api.EmailType[] = [...user.emails].sort(
      (a, b) => new Date(a.date_added).getTime() - new Date(b.date_added).getTime()
    );
    const primaryIndex = sortedData.findIndex((item: Api.EmailType) => item.is_primary);

    if (primaryIndex !== -1) {
      const primaryItem = sortedData.splice(primaryIndex, 1)[0];
      sortedData.unshift(primaryItem);
    }
    return sortedData;
  });
  const [emailField, setEmailField] = useState<boolean>(false);
  const [verifyModal, setVerifyModal] = useState<boolean>(false);
  const [newEmail, setNewEmail] = useState<string>('');
  const [selectedEmail, setSelectedEmail] = useState<string>('');
  const [emailStatus, setEmailStatus] = useState<'' | 'warning' | 'error'>('');

  const { t } = useTranslation();
  const { setAlert } = useAppContext();

  function updateUser(res: Api.UserObject | ResponseObject) {
    if (res && (res as Api.UserObject).user_id) {
      onUpdate(res as Api.UserObject);
      if (emailField) {
        setNewEmail('');
      }
    }
  }

  const { mutate: addEmail, isLoading } = useAddEmail({
    email: newEmail,
    onSuccess: updateUser
  });
  const { mutate: removeEmail, isLoading: isDeleting } = useForgetEmail({
    email: selectedEmail,
    onSuccess: updateUser
  });
  const { mutate: makePrimaryEmail, isLoading: isMakingPrimary } = useMakePrimaryEmail({
    email: selectedEmail,
    onSuccess: updateUser
  });
  const { mutate: verifyEmail, isLoading: isVerifying } = useVerifyEmail({
    email: selectedEmail,
    onSuccess: () => {
      if (verifyModal) {
        setAlert('EMAIL_WAS_RESENT');
      } else {
        setVerifyModal(true);
      }
    }
  });

  useEffect(() => {
    if (user.emails) {
      const sortedData: Api.EmailType[] = [...user.emails].sort(
        (a, b) => new Date(a.date_added).getTime() - new Date(b.date_added).getTime()
      );
      const primaryIndex = sortedData.findIndex((item: Api.EmailType) => item.is_primary);

      if (primaryIndex !== -1) {
        const primaryItem = sortedData.splice(primaryIndex, 1)[0];
        sortedData.unshift(primaryItem);
      }
      setEmails(sortedData);
    }
  }, [user.emails]);

  function onEmailChange(e: React.ChangeEvent<HTMLInputElement>) {
    setNewEmail(e.target.value);
    if (emailStatus) {
      setEmailStatus('');
    }
  }

  function handleAddEmail() {
    if (!emailRegex.test(newEmail)) {
      setEmailStatus('error');
      return;
    }
    if (emailStatus) {
      setEmailStatus('');
    }
    addEmail();
  }

  function handleRemoveEmail(email: string) {
    setSelectedEmail(email);
    setTimeout(() => removeEmail(), 0);
  }

  function handleVerify(email: string) {
    setSelectedEmail(email);
    setTimeout(() => verifyEmail(), 0);
  }

  function handleMakePrimary(email: string) {
    setSelectedEmail(email);
    setTimeout(() => makePrimaryEmail(), 0);
  }

  function menuRender(item: Api.EmailType) {
    return (
      <div className={styles.dropdown}>
        {!item.is_primary && item.is_verified && (
          <div className={styles.dropdownItem} onClick={() => handleMakePrimary(item.email)}>
            {selectedEmail === item.email && isMakingPrimary ? (
              <Loader className={styles.animate} size={16} />
            ) : (
              <Star size={16} />
            )}
            <span>{t('account.makePrimary')}</span>
          </div>
        )}
        {!item.is_primary && !item.is_verified && (
          <div className={styles.dropdownItem} onClick={() => handleVerify(item.email)}>
            {selectedEmail === item.email && isVerifying && !verifyModal ? (
              <Loader className={styles.animate} size={16} />
            ) : (
              <Check size={16} />
            )}
            <span>{t('account.verification.button')}</span>
          </div>
        )}
        {!item.is_primary && (
          <div
            className={styles.dropdownItem}
            style={{ color: 'red' }}
            onClick={() => handleRemoveEmail(item.email)}
          >
            {selectedEmail === item.email && isDeleting ? (
              <Loader className={styles.animate} size={16} />
            ) : (
              <Trash2 size={16} />
            )}
            <span>{t('account.delete')}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`${styles.section} ${styles.firstSection}`}>
      <div className={styles.sectionInfo}>
        <div className={styles.sectionInfoTitle}>{t('account.emailSection.title')}</div>
        <div className={styles.sectionInfoDescription}>{t('account.emailSection.description')}</div>
      </div>
      <div className={styles.sectionBody}>
        <div className={styles.sectionTable}>
          {emails.map((item, i) => (
            <div className={styles.row} key={item.email}>
              <div className={styles.grow}>
                <TextInput
                  value={item.email}
                  label={
                    item.is_primary && !i
                      ? `${t('account.emailSection.primary')} *`
                      : `${t('account.emailSection.additional')} ${i}`
                  }
                  style={{ color: '#474E54' }}
                  labelTooltip={
                    item.is_primary && (
                      <Tooltip
                        overlayClassName={styles.tooltip}
                        title={t('account.emailSection.tooltip')}
                      >
                        <Info size={16} style={{ color: '#474E5450' }} />
                      </Tooltip>
                    )
                  }
                  disabled
                  prefix={
                    item.is_verified ? (
                      <CheckCircle size={16} style={{ color: '#7AD098', marginRight: '5px' }} />
                    ) : (
                      <Tooltip title={t('account.emailSection.verify')}>
                        <AlertCircle size={16} style={{ color: 'red', marginRight: '5px' }} />
                      </Tooltip>
                    )
                  }
                  suffix={
                    <div>
                      <div className={styles.smBlock}>
                        {(!item.is_primary || !item.is_verified) && (
                          <Dropdown
                            dropdownRender={() => menuRender(item)}
                            placement="bottomRight"
                            trigger={['click']}
                          >
                            <MoreHorizontal size={16} color="black" style={{ cursor: 'pointer' }} />
                          </Dropdown>
                        )}
                      </div>
                      <div className={styles.smNone}>
                        {!item.is_primary &&
                          (selectedEmail === item.email && isDeleting ? (
                            <div style={{ width: '16px', height: '16px' }}>
                              <Spinner />
                            </div>
                          ) : (
                            <Trash2
                              size={16}
                              onClick={() => handleRemoveEmail(item.email)}
                              color="red"
                              style={{ cursor: 'pointer' }}
                            />
                          ))}
                      </div>
                    </div>
                  }
                />
              </div>
              <div className={styles.fitContent}>
                {!item.is_primary && !item.is_verified && (
                  <Button
                    type="button"
                    isLoading={selectedEmail === item.email && isVerifying && !verifyModal}
                    onClick={() => handleVerify(item.email)}
                    className={styles.emailButton}
                  >
                    {t('account.verification.button')}
                  </Button>
                )}
                {!item.is_primary && item.is_verified && (
                  <Button
                    type="button"
                    isLoading={selectedEmail === item.email && isMakingPrimary}
                    onClick={() => handleMakePrimary(item.email)}
                    className={styles.emailButton}
                  >
                    {t('account.makePrimary')}
                  </Button>
                )}
              </div>
            </div>
          ))}
          {emailField && (
            <div className={styles.row}>
              <div className={styles.grow}>
                <div className={styles.sectionBodyItem}>
                  <TextInput
                    value={newEmail}
                    status={emailStatus}
                    label={`${t('account.emailSection.additional')} ${emails.length}`}
                    suffix={
                      <X size={16} onClick={() => setNewEmail('')} style={{ cursor: 'pointer' }} />
                    }
                    onChange={onEmailChange}
                  />
                  <div className={styles.emailButtons}>
                    <Button
                      type="button"
                      isLoading={isLoading}
                      theme="cta"
                      style={{ padding: '5px 35px' }}
                      onClick={handleAddEmail}
                    >
                      {t('account.add')}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setEmailField(false)}
                      className={styles.emailButton}
                    >
                      {t('account.cancel')}
                    </Button>
                  </div>
                </div>
              </div>
              <div className={styles.fitContent} />
            </div>
          )}
        </div>
        <Button
          type="button"
          onClick={() => setEmailField(true)}
          disabled={emailField}
          className={`${styles.emailButton} ${styles.emailIconButton}`}
        >
          <Plus color={!emailField ? 'black' : '#474e5480'} />
          <span>{t('account.emailSection.addEmail')}</span>
        </Button>
      </div>
      {verifyModal && (
        <EmailVerificationModal onClose={() => setVerifyModal(false)} onRetry={verifyEmail} />
      )}
    </div>
  );
}
